import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const normalize = (value) =>
  value === null || value === undefined ? "" : String(value).trim().toLowerCase();

const boolTopDealer = (item = {}) =>
  item.top_outlet === true || item.topOutlet === true || item.topDealer === true;

const tokenHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  if (!token) return {};
  return { Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` };
};

const getCode = (row = {}) =>
  String(row.code || row.employeeCode || row.empCode || row.userCode || "").trim();

const getName = (row = {}) =>
  String(row.name || row.employeeName || row.fullName || "").trim();

const getRole = (row = {}) =>
  String(row.role || row?.metadata?.role || row?.user?.role || "").trim();

const getPosition = (row = {}) =>
  String(
    row.position ||
      row.userPosition ||
      row.designation ||
      row?.metadata?.position ||
      row?.user?.position ||
      ""
  ).trim();

const getZoneFromRow = (row = {}) =>
  String(row.zone || row?.metadata?.zone || row?.user?.zone || "").trim();

const getDistrictFromRow = (row = {}) =>
  String(row.district || row?.metadata?.district || row?.user?.district || "").trim();

const getTalukaFromRow = (row = {}) =>
  String(row.taluka || row?.metadata?.taluka || row?.user?.taluka || "").trim();

const getTownFromRow = (row = {}) =>
  String(row.town || row?.metadata?.town || row?.user?.town || "").trim();

const toDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateKey = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const getDateRangeKeys = (startKey, endKey) => {
  const start = parseDateKey(startKey);
  const end = parseDateKey(endKey || startKey);
  if (!start || !end) return [];

  const first = start <= end ? start : end;
  const last = start <= end ? end : start;
  const keys = [];
  const cursor = new Date(first);

  while (cursor <= last && keys.length < 370) {
    keys.push(toDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return keys;
};

const formatDisplayDate = (dateKey) => {
  const date = parseDateKey(dateKey);
  if (!date) return "";
  return date.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const parseCodesFromValue = (value) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => parseCodesFromValue(item));
  }
  if (typeof value === "object") {
    return parseCodesFromValue(value.code || value.userCode || value.employeeCode || "");
  }

  const normalized = String(value)
    .replace(/[|;/]/g, ",")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

  return normalized;
};

const dedupeByCode = (rows = []) => {
  const map = new Map();
  rows.forEach((row) => {
    const code = getCode(row) || row.code;
    if (!code) return;
    const key = normalize(code);
    if (!map.has(key)) {
      map.set(key, { ...row, code });
    }
  });
  return Array.from(map.values());
};

const normalizeFlowTypes = (flowTypes) => {
  if (Array.isArray(flowTypes)) return flowTypes;
  if (typeof flowTypes === "string") {
    return flowTypes
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
};

const collectExistingFromWeeklyRows = (weeklyRows = []) => {
  const map = {};
  weeklyRows.forEach((actor) => {
    const actorCode = getCode(actor).toLowerCase();
    if (!actorCode) return;
    const dealers = Array.isArray(actor.schedule) ? actor.schedule : [];
    map[actorCode] = new Set(
      dealers
        .map((dealer) => getCode(dealer).toLowerCase())
        .filter(Boolean)
    );
  });
  return map;
};

const hierarchySystemFields = new Set([
  "_id",
  "id",
  "firm_code",
  "firm",
  "hierarchy_name",
  "createdat",
  "updatedat",
  "__v",
]);

const resolveHierarchyFields = (rows = [], columns = []) => {
  const resolvedColumns =
    Array.isArray(columns) && columns.length
      ? columns
      : Array.from(new Set(rows.flatMap((row) => Object.keys(row || {}))));

  const actorFields = [];
  const dealerFields = [];

  resolvedColumns.forEach((field) => {
    const key = normalize(field);
    if (!key || hierarchySystemFields.has(key)) return;
    if (key === "dealer" || key === "mdd") {
      dealerFields.push(field);
      return;
    }
    actorFields.push(field);
  });

  return { actorFields, dealerFields };
};

const buildDealerSeedsFromRows = (rows = [], dealerFields = []) => {
  const dealerSeeds = [];

  rows.forEach((row) => {
    dealerFields.forEach((field) => {
      const position = String(field || "").trim();
      const defaultRole = normalize(field) === "mdd" ? "mdd" : "dealer";
      parseCodesFromValue(row?.[field]).forEach((code) => {
        dealerSeeds.push({
          code,
          position,
          role: defaultRole,
        });
      });
    });
  });

  return dedupeByCode(dealerSeeds);
};

const buildHierarchySeeds = (rows = [], columns = []) => {
  const { actorFields, dealerFields } = resolveHierarchyFields(rows, columns);

  const actorSeeds = [];

  rows.forEach((row) => {
    actorFields.forEach((field) => {
      const position = String(field || "").trim();
      parseCodesFromValue(row?.[field]).forEach((code) => {
        actorSeeds.push({
          code,
          position,
          role: "employee",
        });
      });
    });
  });

  return {
    actorSeeds: dedupeByCode(actorSeeds),
    dealerSeeds: buildDealerSeedsFromRows(rows, dealerFields),
  };
};

const isAdminLikeRole = (roleValue = "") => {
  const role = normalize(roleValue);
  return role === "admin" || role === "super_admin" || role === "super admin";
};

const buildDealerSeedsForSelectedActors = ({
  rows = [],
  columns = [],
  selectedActorCodes = [],
  actors = [],
}) => {
  const { actorFields, dealerFields } = resolveHierarchyFields(rows, columns);
  const selectedSet = new Set(selectedActorCodes.map((code) => normalize(code)).filter(Boolean));

  if (selectedSet.size === 0) {
    return buildDealerSeedsFromRows(rows, dealerFields);
  }

  const actorByCode = new Map(actors.map((actor) => [normalize(actor.code), actor]));
  const hasAdminActor = Array.from(selectedSet).some((code) =>
    isAdminLikeRole(actorByCode.get(code)?.role)
  );

  if (hasAdminActor) {
    return buildDealerSeedsFromRows(rows, dealerFields);
  }

  const matchedRows = rows.filter((row) =>
    actorFields.some((field) =>
      parseCodesFromValue(row?.[field]).some((code) => selectedSet.has(normalize(code)))
    )
  );

  return buildDealerSeedsFromRows(matchedRows, dealerFields);
};

const mapDealerSeedsToRows = (dealerSeeds = [], userByCode = {}) =>
  dealerSeeds
    .map((seed) => {
      const row = userByCode[normalize(seed.code)] || {};
      const code = getCode(row) || seed.code;
      return {
        code,
        name: getName(row) || code,
        role: getRole(row) || seed.role || "dealer",
        position: getPosition(row) || seed.position || "",
        zone: getZoneFromRow(row),
        district: getDistrictFromRow(row),
        taluka: getTalukaFromRow(row),
        town: getTownFromRow(row),
        topDealer: boolTopDealer(row),
      };
    })
    .filter((row) => row.code);

const toDateInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const isConfigExpired = (cfg = {}, todayStart = null) => {
  const expiryRaw = cfg.expiryDate || cfg.endDate || cfg.expiry || cfg.validTill || "";
  if (!expiryRaw) return false;
  const expiry = new Date(expiryRaw);
  if (Number.isNaN(expiry.getTime())) return false;
  const cutoff = todayStart || new Date(new Date().toDateString());
  return expiry < cutoff;
};

function BeatSetupModal({
  open,
  onClose,
  backendUrl,
  weeklyRows = [],
  onApplied,
  initialConfig = null,
}) {
  const [loadingBase, setLoadingBase] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [firms, setFirms] = useState([]);
  const [metaFlows, setMetaFlows] = useState([]);
  const [adminFlowTypes, setAdminFlowTypes] = useState([]);
  const [firmCode, setFirmCode] = useState("");
  const [flowName, setFlowName] = useState("");

  const [actors, setActors] = useState([]);
  const [selectedActorCodes, setSelectedActorCodes] = useState([]);
  const [pendingInitialActorCodes, setPendingInitialActorCodes] = useState([]);
  const [actorSearch, setActorSearch] = useState("");
  const [hierarchyRows, setHierarchyRows] = useState([]);
  const [hierarchyColumns, setHierarchyColumns] = useState([]);
  const [userByCodeMap, setUserByCodeMap] = useState({});

  const [dealerMddList, setDealerMddList] = useState([]);
  const [selectedDealerCodes, setSelectedDealerCodes] = useState([]);
  const [dealerSearch, setDealerSearch] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [talukaFilter, setTalukaFilter] = useState("");
  const [townFilter, setTownFilter] = useState("");
  const [topDealerFilter, setTopDealerFilter] = useState("all");

  const [configByActorCode, setConfigByActorCode] = useState({});
  const [startDate, setStartDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [pendingInitialDealerCodes, setPendingInitialDealerCodes] = useState([]);
  const [actorDealerLoading, setActorDealerLoading] = useState(false);

  const selectedFirm = useMemo(
    () => firms.find((f) => String(f.code || "") === String(firmCode || "")),
    [firms, firmCode]
  );

  const selectedPlanDates = useMemo(
    () => getDateRangeKeys(startDate, expiryDate),
    [startDate, expiryDate]
  );

  const calendarCells = useMemo(() => {
    const monthStart = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const monthEnd = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
    const firstDayOffset = (monthStart.getDay() + 6) % 7;
    const totalCells = Math.ceil((firstDayOffset + monthEnd.getDate()) / 7) * 7;
    const gridStart = addDays(monthStart, -firstDayOffset);
    const todayKey = toDateKey(new Date());
    const selectedSet = new Set(selectedPlanDates);

    return Array.from({ length: totalCells }, (_, index) => {
      const date = addDays(gridStart, index);
      const key = toDateKey(date);
      return {
        key,
        label: date.getDate(),
        weekday: date.toLocaleDateString("en-IN", { weekday: "short" }),
        isCurrentMonth: date.getMonth() === calendarMonth.getMonth(),
        isToday: key === todayKey,
        isSelected: selectedSet.has(key),
        isRangeStart: key === startDate,
        isRangeEnd: key === (expiryDate || startDate),
      };
    });
  }, [calendarMonth, selectedPlanDates, startDate, expiryDate]);

  const calendarMonthLabel = useMemo(
    () =>
      calendarMonth.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      }),
    [calendarMonth]
  );

  const flowOptions = useMemo(() => {
    const flowTypes = normalizeFlowTypes(selectedFirm?.flowTypes);

    if (flowTypes.length === 0) {
      const flowSource = metaFlows.length > 0 ? metaFlows : adminFlowTypes;
      return flowSource
        .map((flow) => {
          if (typeof flow === "string") {
            return { name: flow, label: flow };
          }
          const name = String(flow?.name || flow?.flowName || flow?.value || "").trim();
          if (!name) return null;
          return {
            name,
            label: String(flow?.label || flow?.name || name).trim(),
          };
        })
        .filter(Boolean);
    }

    return flowTypes
      .map((flow) => {
        if (typeof flow === "string") {
          return { name: flow, label: flow };
        }
        const name = String(flow?.name || flow?.flowName || flow?.value || "").trim();
        if (!name) return null;
        return {
          name,
          label: String(flow?.label || flow?.name || name).trim(),
        };
      })
      .filter(Boolean);
  }, [selectedFirm, metaFlows, adminFlowTypes]);

  const weeklyConfigByActorCode = useMemo(
    () => collectExistingFromWeeklyRows(weeklyRows),
    [weeklyRows]
  );

  const weeklyActorCodeSet = useMemo(
    () => new Set(weeklyRows.map((row) => normalize(getCode(row))).filter(Boolean)),
    [weeklyRows]
  );

  const resetModalState = () => {
    setError("");
    setFirmCode("");
    setFlowName("");
    setActors([]);
    setSelectedActorCodes([]);
    setPendingInitialActorCodes([]);
    setActorSearch("");
    setHierarchyRows([]);
    setHierarchyColumns([]);
    setUserByCodeMap({});
    setDealerMddList([]);
    setSelectedDealerCodes([]);
    setDealerSearch("");
    setZoneFilter("");
    setDistrictFilter("");
    setTalukaFilter("");
    setTownFilter("");
    setTopDealerFilter("all");
    setConfigByActorCode({});
    setStartDate("");
    setExpiryDate("");
    setCalendarMonth(new Date());
    setPendingInitialDealerCodes([]);
  };

  const fetchPagedUsersByRole = async (roleParam) => {
    const list = [];
    let page = 1;
    const limit = 1000;
    let hasMore = true;
    while (hasMore && page <= 30) {
      const res = await axios.get(`${backendUrl}/user/get-by-admins`, {
        params: {
          page,
          limit,
          sort: "createdAt",
          order: -1,
          search: "",
          role: roleParam,
        },
        headers: tokenHeaders(),
      });
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      list.push(...rows);
      const totalRecords = Number(res?.data?.totalRecords || 0);
      hasMore = rows.length === limit && page * limit < totalRecords;
      page += 1;
    }
    return list;
  };

  const fetchUserDirectoryRows = async () => {
    const list = [];
    let page = 1;
    const limit = 1000;
    let hasMore = true;
    while (hasMore && page <= 30) {
      const res = await axios.get(`${backendUrl}/super-admin/user-directory`, {
        params: {
          search: "",
          position: "",
          role: "",
          status: "",
          firm_code: "",
          page,
          limit,
        },
        headers: tokenHeaders(),
      });
      const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      list.push(...rows);
      const total = Number(res?.data?.total || 0);
      hasMore = rows.length === limit && page * limit < total;
      page += 1;
    }
    return list;
  };

  const fetchUsersByCodes = async (codes = []) => {
    const needed = new Set(codes.map((code) => normalize(code)).filter(Boolean));
    if (needed.size === 0) return [];
    const found = new Map();

    try {
      let page = 1;
      const limit = 1000;
      let hasMore = true;
      while (hasMore && page <= 30 && found.size < needed.size) {
        const res = await axios.get(`${backendUrl}/super-admin/user-directory`, {
          params: {
            search: "",
            position: "",
            role: "",
            status: "",
            firm_code: "",
            page,
            limit,
          },
          headers: tokenHeaders(),
        });
        const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
        rows.forEach((row) => {
          const code = normalize(getCode(row));
          if (!code || !needed.has(code)) return;
          if (!found.has(code)) found.set(code, row);
        });
        const total = Number(res?.data?.total || 0);
        hasMore = rows.length === limit && page * limit < total;
        page += 1;
      }
    } catch (e) {
      // ignore and fallback to empty
    }

    return Array.from(found.values());
  };

  const fetchHierarchyRowsForFlow = async (selectedFirmCode, selectedFlowName) => {
    const firmCodeNorm = normalize(selectedFirmCode);
    const filterByFirmIfPresent = (rows = []) =>
      rows.filter((row) => {
        const rowFirmCode = normalize(row?.firm_code || row?.firmCode || row?.firm || "");
        return !rowFirmCode || rowFirmCode === firmCodeNorm;
      });

    try {
      const res = await axios.get(`${backendUrl}/super-admin/hierarchy`, {
        params: {
          firm_code: selectedFirmCode,
          hierarchy_name: selectedFlowName,
          all: true,
        },
        headers: tokenHeaders(),
      });

      const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      return {
        rows: filterByFirmIfPresent(rows),
        columns: Array.isArray(res?.data?.columns) ? res.data.columns : [],
      };
    } catch (primaryError) {
      const fallbackRes = await axios.get(
        `${backendUrl}/hierarchy-entries/get-hierarchy-entries-for-admin`,
        {
          params: {
            page: 1,
            limit: 10000,
            hierarchy_name: selectedFlowName,
          },
          headers: tokenHeaders(),
        }
      );

      const rows = Array.isArray(fallbackRes?.data?.data) ? fallbackRes.data.data : [];
      const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

      return { rows: filterByFirmIfPresent(rows), columns };
    }
  };

  useEffect(() => {
    if (!open) return;
    resetModalState();

    const loadFirms = async () => {
      setLoadingBase(true);
      try {
        const [dropdownRes, metaRes, actorTypeRes] = await Promise.allSettled([
          axios.get(`${backendUrl}/get-firms-for-dropdown`, {
            headers: tokenHeaders(),
          }),
          axios.get(`${backendUrl}/super-admin/hierarchy/meta`, {
            headers: tokenHeaders(),
          }),
          axios.get(`${backendUrl}/actorTypesHierarchy/get-all-by-admin`, {
            headers: tokenHeaders(),
          }),
        ]);

        const dropdownFirms =
          dropdownRes.status === "fulfilled" && Array.isArray(dropdownRes.value?.data?.data)
            ? dropdownRes.value.data.data
            : [];

        const metaFirms =
          metaRes.status === "fulfilled" && Array.isArray(metaRes.value?.data?.data?.firms)
            ? metaRes.value.data.data.firms
            : [];

        const mergedByCode = new Map();
        dropdownFirms.forEach((firm) => {
          const code = String(firm?.code || "").trim();
          if (!code) return;
          mergedByCode.set(code, { ...firm });
        });

        metaFirms.forEach((firm) => {
          const code = String(firm?.code || "").trim();
          if (!code) return;
          const prev = mergedByCode.get(code) || {};
          const nextFlowTypes =
            normalizeFlowTypes(firm?.flowTypes).length > 0
              ? normalizeFlowTypes(firm?.flowTypes)
              : normalizeFlowTypes(prev?.flowTypes);

          mergedByCode.set(code, {
            ...prev,
            ...firm,
            flowTypes: nextFlowTypes,
          });
        });

        setFirms(Array.from(mergedByCode.values()));
        setMetaFlows(
          metaRes.status === "fulfilled" && Array.isArray(metaRes.value?.data?.data?.flows)
            ? metaRes.value.data.data.flows
            : []
        );
        setAdminFlowTypes(
          actorTypeRes.status === "fulfilled" && Array.isArray(actorTypeRes.value?.data?.data)
            ? actorTypeRes.value.data.data
            : []
        );
      } catch (e) {
        setError("Failed to load firms.");
      } finally {
        setLoadingBase(false);
      }
    };

    loadFirms();
  }, [open, backendUrl]);

  useEffect(() => {
    if (!open || !initialConfig) return;

    const initFirm = String(initialConfig.firmCode || initialConfig.firm_code || "").trim();
    const initFlow = String(initialConfig.flowName || initialConfig.hierarchy_name || "").trim();
    const initActorCodes = Array.isArray(initialConfig.actorCodes)
      ? initialConfig.actorCodes
      : initialConfig.actorCode
      ? [initialConfig.actorCode]
      : [];
    const initDealerCodes = Array.isArray(initialConfig.dealerCodes)
      ? initialConfig.dealerCodes
      : Array.isArray(initialConfig.dealers)
      ? initialConfig.dealers.map((d) => d.code)
      : [];

    setFirmCode(initFirm);
    setFlowName(initFlow);
    setPendingInitialActorCodes(
      initActorCodes.map((code) => String(code || "").trim()).filter(Boolean)
    );
    setSelectedActorCodes(initActorCodes.map((code) => String(code || "").trim()).filter(Boolean));
    setPendingInitialDealerCodes(
      initDealerCodes.map((code) => String(code || "").trim()).filter(Boolean)
    );
    const initStartDate = toDateInputValue(
      initialConfig.startDate ||
        initialConfig.planDate ||
        initialConfig.fromDate ||
        initialConfig.validFrom ||
        ""
    );
    const initExpiryDate = toDateInputValue(
      initialConfig.expiryDate ||
        initialConfig.endDate ||
        initialConfig.validTill ||
        initialConfig.planDate ||
        ""
    );

    setStartDate(initStartDate);
    setExpiryDate(initExpiryDate);
    const monthSource = parseDateKey(initStartDate);
    if (monthSource) {
      setCalendarMonth(new Date(monthSource.getFullYear(), monthSource.getMonth(), 1));
    }
  }, [open, initialConfig]);

  useEffect(() => {
    if (!open || !firmCode || !flowName) {
      setActors([]);
      setDealerMddList([]);
      if (!initialConfig) {
        setSelectedActorCodes([]);
        setSelectedDealerCodes([]);
      }
      setHierarchyRows([]);
      setHierarchyColumns([]);
      setUserByCodeMap({});
      return;
    }

    const loadActorsAndDealers = async () => {
      setActorDealerLoading(true);
      setLoadingBase(true);
      setError("");

      try {
        const hierarchy = await fetchHierarchyRowsForFlow(firmCode, flowName);
        const { actorSeeds } = buildHierarchySeeds(
          hierarchy.rows,
          hierarchy.columns
        );

        const roleFetchers = [
          fetchPagedUsersByRole("employee"),
          fetchPagedUsersByRole("hr"),
          fetchPagedUsersByRole("admin"),
          fetchPagedUsersByRole("super_admin"),
          fetchPagedUsersByRole("dealer"),
          fetchPagedUsersByRole("mdd"),
        ];

        const roleResults = await Promise.allSettled(roleFetchers);
        let sourceRows = roleResults
          .filter((res) => res.status === "fulfilled")
          .flatMap((res) => res.value || []);

        try {
          const directoryRows = await fetchUserDirectoryRows();
          if (Array.isArray(directoryRows) && directoryRows.length > 0) {
            sourceRows = [...sourceRows, ...directoryRows];
          }
        } catch (e) {
          // role-based source remains fallback-safe
        }

        sourceRows = dedupeByCode(sourceRows);
        const userByCode = sourceRows.reduce((acc, row) => {
          const code = normalize(getCode(row));
          if (!code) return acc;
          acc[code] = row;
          return acc;
        }, {});

        const flowActorRows = actorSeeds
          .map((seed) => {
            const row = userByCode[normalize(seed.code)] || {};
            const code = getCode(row) || seed.code;
            return {
              code,
              name: getName(row) || code,
              role: getRole(row) || seed.role || "employee",
              position: getPosition(row) || seed.position || "",
            };
          })
          .filter((row) => row.code);

        const globalAdminActorRows = sourceRows
          .filter((row) => {
            const code = getCode(row);
            if (!code) return false;
            return isAdminLikeRole(getRole(row));
          })
          .map((row) => ({
            code: getCode(row),
            name: getName(row) || getCode(row),
            role: getRole(row),
            position: getPosition(row) || "admin",
          }));

        const selectedActorCodeSet = new Set(
          selectedActorCodes.map((code) => normalize(code)).filter(Boolean)
        );
        const missingSelectedActors = Array.from(selectedActorCodeSet)
          .filter(
            (code) =>
              !flowActorRows.some((row) => normalize(row.code) === code) &&
              !globalAdminActorRows.some((row) => normalize(row.code) === code)
          )
          .map((code) => {
            const row = userByCode[code] || {};
            const resolvedCode = getCode(row) || code;
            return {
              code: resolvedCode,
              name: getName(row) || resolvedCode,
              role: getRole(row) || "employee",
              position: getPosition(row) || "",
            };
          });

        const actorRows = dedupeByCode([
          ...flowActorRows,
          ...globalAdminActorRows,
          ...missingSelectedActors,
        ]);

        const dealerSeedsForSelection = buildDealerSeedsForSelectedActors({
          rows: hierarchy.rows,
          columns: hierarchy.columns,
          selectedActorCodes,
          actors: actorRows,
        });
        const dealerRows = mapDealerSeedsToRows(dealerSeedsForSelection, userByCode);

        setHierarchyRows(hierarchy.rows);
        setHierarchyColumns(hierarchy.columns);
        setUserByCodeMap(userByCode);
        setActors(actorRows);
        setDealerMddList(dedupeByCode(dealerRows));
      } catch (e) {
        setActors([]);
        setHierarchyRows([]);
        setHierarchyColumns([]);
        setUserByCodeMap({});
        setDealerMddList([]);
        setError("Failed to load flow users/dealer mapping.");
      } finally {
        setActorDealerLoading(false);
        setLoadingBase(false);
      }
    };

    loadActorsAndDealers();
  }, [open, firmCode, flowName, weeklyActorCodeSet]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !firmCode || !flowName || hierarchyRows.length === 0) return;

    const dealerSeedsForSelection = buildDealerSeedsForSelectedActors({
      rows: hierarchyRows,
      columns: hierarchyColumns,
      selectedActorCodes,
      actors,
    });
    const nextDealerRows = dedupeByCode(
      mapDealerSeedsToRows(dealerSeedsForSelection, userByCodeMap)
    );
    const nextDealerSet = new Set(nextDealerRows.map((row) => normalize(row.code)));

    setDealerMddList(nextDealerRows);
    if (pendingInitialDealerCodes.length === 0) {
      setSelectedDealerCodes((prev) =>
        prev.filter((code) => nextDealerSet.has(normalize(code)))
      );
    }
  }, [
    open,
    firmCode,
    flowName,
    hierarchyRows,
    hierarchyColumns,
    selectedActorCodes,
    actors,
    userByCodeMap,
    pendingInitialDealerCodes,
  ]);

  const visibleActors = useMemo(() => {
    const q = normalize(actorSearch);
    const searched = !q
      ? actors
      : actors.filter((actor) =>
      [actor.name, actor.code, actor.position, actor.role].some((v) =>
        normalize(v).includes(q)
      )
    );

    // Once actor is selected, keep the panel focused on selected actors only.
    if (selectedActorCodes.length > 0) {
      const selectedSet = new Set(selectedActorCodes.map((code) => normalize(code)));
      return searched.filter((actor) => selectedSet.has(normalize(actor.code)));
    }

    return searched;
  }, [actors, actorSearch, selectedActorCodes]);

  const visibleDealerMdds = useMemo(() => {
    const q = normalize(dealerSearch);
    return dealerMddList.filter((item) => {
      const searchMatch =
        !q ||
        [
          item.name,
          item.code,
          item.position,
          item.role,
          item.zone,
          item.district,
          item.taluka,
          item.town,
        ].some((v) => normalize(v).includes(q));
      const zoneMatch = !zoneFilter || normalize(item.zone) === normalize(zoneFilter);
      const districtMatch =
        !districtFilter || normalize(item.district) === normalize(districtFilter);
      const talukaMatch = !talukaFilter || normalize(item.taluka) === normalize(talukaFilter);
      const townMatch = !townFilter || normalize(item.town) === normalize(townFilter);
      const topMatch =
        topDealerFilter === "all" ||
        (topDealerFilter === "yes" && item.topDealer) ||
        (topDealerFilter === "no" && !item.topDealer);
      return searchMatch && zoneMatch && districtMatch && talukaMatch && townMatch && topMatch;
    });
  }, [
    dealerMddList,
    dealerSearch,
    zoneFilter,
    districtFilter,
    talukaFilter,
    townFilter,
    topDealerFilter,
  ]);

  const zoneOptions = useMemo(
    () =>
      Array.from(new Set(dealerMddList.map((row) => row.zone).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [dealerMddList]
  );

  const districtOptions = useMemo(
    () =>
      Array.from(new Set(dealerMddList.map((row) => row.district).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [dealerMddList]
  );

  const talukaOptions = useMemo(
    () =>
      Array.from(new Set(dealerMddList.map((row) => row.taluka).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [dealerMddList]
  );

  const townOptions = useMemo(
    () =>
      Array.from(new Set(dealerMddList.map((row) => row.town).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [dealerMddList]
  );

  useEffect(() => {
    if (pendingInitialActorCodes.length === 0) return;
    if (actors.length === 0) return;

    const allowed = new Set(actors.map((row) => normalize(row.code)));
    const resolved = pendingInitialActorCodes
      .map((code) => String(code || "").trim())
      .filter((code) => allowed.has(normalize(code)));

    if (resolved.length > 0) {
      setSelectedActorCodes(resolved);
    }
    setPendingInitialActorCodes([]);
  }, [actors, pendingInitialActorCodes]);

  useEffect(() => {
    if (pendingInitialDealerCodes.length === 0) return;
    if (dealerMddList.length === 0) return;

    const allowed = new Set(dealerMddList.map((row) => normalize(row.code)));
    const resolved = pendingInitialDealerCodes
      .map((code) => String(code || "").trim())
      .filter((code) => allowed.has(normalize(code)));

    if (resolved.length > 0) {
      setSelectedDealerCodes(resolved);
    }
    setPendingInitialDealerCodes([]);
  }, [dealerMddList, pendingInitialDealerCodes]);

  const allVisibleActorsSelected =
    visibleActors.length > 0 &&
    visibleActors.every((actor) => selectedActorCodes.includes(actor.code));

  const allVisibleDealerSelected =
    visibleDealerMdds.length > 0 &&
    visibleDealerMdds.every((dealer) => selectedDealerCodes.includes(dealer.code));

  const hydrateFromExistingConfig = (actorCodes = [], externalConfig = null) => {
    const resolvedConfig = externalConfig || configByActorCode;
    const sets = actorCodes.map((code) => {
      const key = normalize(code);
      return resolvedConfig[key] || weeklyConfigByActorCode[key] || new Set();
    });

    if (sets.length === 0) {
      setSelectedDealerCodes([]);
      return;
    }

    const intersection = new Set(sets[0]);
    sets.slice(1).forEach((setRef) => {
      Array.from(intersection).forEach((value) => {
        if (!setRef.has(value)) intersection.delete(value);
      });
    });

    const codeMap = new Map(dealerMddList.map((row) => [normalize(row.code), row.code]));
    const resolvedCodes = Array.from(intersection)
      .map((code) => codeMap.get(code))
      .filter(Boolean);

    setSelectedDealerCodes(resolvedCodes);
  };

  const fetchSavedConfigForActors = async (actorCodes = []) => {
    if (actorCodes.length === 0) return;

    try {
      if (selectedPlanDates.length > 0) {
        try {
          const planRes = await axios.post(
            `${backendUrl}/admin/beat-mapping/plans/get`,
            { actorCodes, dates: selectedPlanDates },
            { headers: { "Content-Type": "application/json", ...tokenHeaders() } }
          );
          const plans = Array.isArray(planRes?.data?.plans) ? planRes.data.plans : [];
          if (plans.length > 0) {
            const planMap = {};
            actorCodes.forEach((actorCode) => {
              const actorPlans = plans.filter(
                (plan) => normalize(plan.actorCode || plan.code) === normalize(actorCode)
              );
              const planSets = actorPlans.map((plan) => {
                const dealerCodes = Array.isArray(plan.dealerCodes)
                  ? plan.dealerCodes
                  : Array.isArray(plan.dealers)
                  ? plan.dealers.map((dealer) => dealer.code)
                  : [];
                return new Set(dealerCodes.map((code) => normalize(code)).filter(Boolean));
              });
              if (planSets.length === 0) return;
              const intersection = new Set(planSets[0]);
              planSets.slice(1).forEach((setRef) => {
                Array.from(intersection).forEach((value) => {
                  if (!setRef.has(value)) intersection.delete(value);
                });
              });
              planMap[normalize(actorCode)] = intersection;
            });

            setConfigByActorCode((prev) => ({ ...prev, ...planMap }));
            hydrateFromExistingConfig(actorCodes, planMap);
            return;
          }
        } catch (planError) {
          // Fall back to the legacy config source.
        }
      }

      const res = await axios.post(
        `${backendUrl}/admin/beat-mapping/config/get`,
        { actorCodes },
        { headers: { "Content-Type": "application/json", ...tokenHeaders() } }
      );

      const configs = Array.isArray(res?.data?.configs) ? res.data.configs : [];
      const map = {};
      const todayStart = new Date(new Date().toDateString());
      const actorCodeSet = new Set(actorCodes.map((code) => normalize(code)).filter(Boolean));
      const activeConfigs = configs.filter((cfg) => {
        const actorCode = normalize(cfg.actorCode || cfg.code);
        if (!actorCodeSet.has(actorCode)) return false;
        return !isConfigExpired(cfg, todayStart);
      });

      const allConfigDealerCodes = new Set();

      activeConfigs.forEach((cfg) => {
        const key = normalize(cfg.actorCode || cfg.code);
        if (!key) return;

        const dealerCodes = Array.isArray(cfg.dealerCodes)
          ? cfg.dealerCodes
          : Array.isArray(cfg.dealers)
          ? cfg.dealers.map((d) => d.code)
          : [];

        const normalizedDealers = dealerCodes.map((code) => normalize(code)).filter(Boolean);
        map[key] = new Set(normalizedDealers);
        normalizedDealers.forEach((code) => allConfigDealerCodes.add(code));
      });

      setConfigByActorCode((prev) => ({ ...prev, ...map }));
      hydrateFromExistingConfig(actorCodes, map);

      if (actorCodes.length === 1) {
        const selectedActor = normalize(actorCodes[0]);
        const cfg = activeConfigs.find((item) => normalize(item.actorCode || item.code) === selectedActor);
        if (cfg) {
          if (selectedPlanDates.length === 0) {
            setStartDate(toDateInputValue(cfg.startDate || cfg.fromDate || cfg.validFrom || ""));
            setExpiryDate(toDateInputValue(cfg.expiryDate || cfg.endDate || cfg.validTill || ""));
          }
        } else if (!initialConfig) {
          if (selectedPlanDates.length === 0) {
            setStartDate("");
            setExpiryDate("");
          }
        }
      }

      const existingDealerSet = new Set(dealerMddList.map((row) => normalize(row.code)));
      const missingCodes = Array.from(allConfigDealerCodes).filter((code) => !existingDealerSet.has(code));
      if (missingCodes.length > 0) {
        const missingRows = await fetchUsersByCodes(missingCodes);
        const mappedMissing = missingRows
          .map((row) => ({
            code: getCode(row),
            name: getName(row) || getCode(row),
            role: getRole(row) || "dealer",
            position: getPosition(row) || "",
            zone: getZoneFromRow(row),
            district: getDistrictFromRow(row),
            taluka: getTalukaFromRow(row),
            town: getTownFromRow(row),
            topDealer: boolTopDealer(row),
          }))
          .filter((row) => row.code);
        if (mappedMissing.length > 0) {
          setDealerMddList((prev) => dedupeByCode([...prev, ...mappedMissing]));
        }
      }
    } catch (e) {
      hydrateFromExistingConfig(actorCodes);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (selectedActorCodes.length === 0) {
      setSelectedDealerCodes([]);
      return;
    }

    fetchSavedConfigForActors(selectedActorCodes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedActorCodes.join("|"), selectedPlanDates.join("|"), dealerMddList.length]);

  const toggleActor = (code) => {
    setSelectedActorCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const toggleDealer = (code) => {
    setSelectedDealerCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const toggleSelectAllActors = () => {
    if (allVisibleActorsSelected) {
      const visibleCodeSet = new Set(visibleActors.map((a) => a.code));
      setSelectedActorCodes((prev) => prev.filter((c) => !visibleCodeSet.has(c)));
      return;
    }

    setSelectedActorCodes((prev) => {
      const next = new Set(prev);
      visibleActors.forEach((actor) => next.add(actor.code));
      return Array.from(next);
    });
  };

  const toggleSelectAllDealers = () => {
    if (allVisibleDealerSelected) {
      const visibleCodeSet = new Set(visibleDealerMdds.map((d) => d.code));
      setSelectedDealerCodes((prev) => prev.filter((c) => !visibleCodeSet.has(c)));
      return;
    }

    setSelectedDealerCodes((prev) => {
      const next = new Set(prev);
      visibleDealerMdds.forEach((dealer) => next.add(dealer.code));
      return Array.from(next);
    });
  };

  const handleCalendarDateSelect = (dateKey) => {
    if (!dateKey) return;
    if (!startDate || (startDate && expiryDate)) {
      setStartDate(dateKey);
      setExpiryDate("");
      return;
    }
    if (dateKey < startDate) {
      setExpiryDate(startDate);
      setStartDate(dateKey);
      return;
    }
    setExpiryDate(dateKey);
  };

  const moveCalendarMonth = (direction) => {
    setCalendarMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + direction, 1)
    );
  };

  const clearCalendarSelection = () => {
    setStartDate("");
    setExpiryDate("");
  };

  const selectedDealerRows = useMemo(() => {
    const selectedSet = new Set(selectedDealerCodes.map((code) => normalize(code)));
    return dealerMddList.filter((item) => selectedSet.has(normalize(item.code)));
  }, [dealerMddList, selectedDealerCodes]);

  const selectedVisibleDealerCount = useMemo(() => {
    const visibleSet = new Set(visibleDealerMdds.map((item) => normalize(item.code)));
    return selectedDealerCodes.filter((code) => visibleSet.has(normalize(code))).length;
  }, [visibleDealerMdds, selectedDealerCodes]);

  const handleSaveAndApply = async () => {
    if (selectedPlanDates.length === 0) {
      setError("Please select at least one plan date.");
      return;
    }
    if (selectedPlanDates.length >= 370) {
      setError("Please keep a plan range below one year.");
      return;
    }
    if (!firmCode) {
      setError("Please select a firm.");
      return;
    }
    if (!flowName) {
      setError("Please select a flow.");
      return;
    }
    if (selectedActorCodes.length === 0) {
      setError("Please select at least one actor.");
      return;
    }
    if (selectedDealerRows.length === 0) {
      setError("Please select at least one dealer/MDD.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const todayKey = toDateKey(new Date());
      const shouldApplyToday = selectedPlanDates.includes(todayKey);
      const setupPayload = {
        firmCode,
        flowName,
        dates: selectedPlanDates,
        startDate: selectedPlanDates[0],
        expiryDate: selectedPlanDates[selectedPlanDates.length - 1],
        actorCodes: selectedActorCodes,
        dealers: selectedDealerRows.map((row) => ({
          name: row.name,
          code: row.code,
          zone: row.zone || "",
          district: row.district || "",
          taluka: row.taluka || "",
          town: row.town || "",
          top_dealer: row.topDealer === true,
          role: row.role || "",
          position: row.position || "",
        })),
      };

      const planRes = await axios.put(
        `${backendUrl}/admin/beat-mapping/plans/upsert`,
        setupPayload,
        { headers: { "Content-Type": "application/json", ...tokenHeaders() } }
      );

      let applyMessage = "";
      if (shouldApplyToday) {
        const applyRes = await axios.put(
          `${backendUrl}/add-daily-beat-mapping`,
          {
            mode: "from_setup",
            ...setupPayload,
          },
          { headers: { "Content-Type": "application/json", ...tokenHeaders() } }
        );
        applyMessage = applyRes?.data?.message || "";
      }

      const savedCount = Number(planRes?.data?.updatedPlans || 0);
      const msg = shouldApplyToday
        ? applyMessage || "Beat plans saved and today's mapping applied."
        : `Beat plans saved for ${selectedPlanDates.length} day${
            selectedPlanDates.length === 1 ? "" : "s"
          }${savedCount ? ` (${savedCount} actor-day plans).` : "."}`;

      onApplied?.({
        type: "success",
        message: msg,
      });
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          "Failed to save/apply setup. Backend endpoint may be pending."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="beat-setup-modal-overlay" onClick={onClose}>
      <div className="beat-setup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="beat-setup-modal__header">
          <div>
            <h2>Beat Setup</h2>
            <p>Plan beat trips by date, actor and dealer/MDD coverage.</p>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="beat-setup-modal__body">
          <div className="beat-plan-calendar-card">
            <div className="calendar-copy">
              <span>Calendar Planner</span>
              <h3>
                {selectedPlanDates.length > 0
                  ? `${selectedPlanDates.length} day${
                      selectedPlanDates.length === 1 ? "" : "s"
                    } selected`
                  : "Pick trip date or range"}
              </h3>
              <p>
                {selectedPlanDates.length > 0
                  ? `${formatDisplayDate(selectedPlanDates[0])}${
                      selectedPlanDates.length > 1
                        ? ` to ${formatDisplayDate(
                            selectedPlanDates[selectedPlanDates.length - 1]
                          )}`
                        : ""
                    }`
                  : "Click a date once for single-day planning, then another date for a range."}
              </p>
              <div className="calendar-actions">
                <button type="button" onClick={() => moveCalendarMonth(-1)}>
                  ‹
                </button>
                <strong>{calendarMonthLabel}</strong>
                <button type="button" onClick={() => moveCalendarMonth(1)}>
                  ›
                </button>
                <button type="button" onClick={clearCalendarSelection}>
                  Clear
                </button>
              </div>
            </div>

            <div className="beat-calendar">
              <div className="weekday-row">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>
              <div className="calendar-grid">
                {calendarCells.map((cell) => (
                  <button
                    key={cell.key}
                    type="button"
                    className={[
                      "calendar-day",
                      cell.isCurrentMonth ? "" : "muted",
                      cell.isToday ? "today" : "",
                      cell.isSelected ? "selected" : "",
                      cell.isRangeStart ? "range-start" : "",
                      cell.isRangeEnd ? "range-end" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => handleCalendarDateSelect(cell.key)}
                    disabled={saving}
                  >
                    <span>{cell.label}</span>
                    <small>{cell.weekday}</small>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="beat-setup-top-row double">
            <div>
              <label>Firm</label>
              <select
                value={firmCode}
                onChange={(e) => {
                  setFirmCode(e.target.value);
                  setFlowName("");
                  setActors([]);
                  setHierarchyRows([]);
                  setHierarchyColumns([]);
                  setUserByCodeMap({});
                  setDealerMddList([]);
                  setSelectedActorCodes([]);
                  setSelectedDealerCodes([]);
                  setActorSearch("");
                  setDealerSearch("");
                  setZoneFilter("");
                  setDistrictFilter("");
                  setTalukaFilter("");
                  setTownFilter("");
                  setTopDealerFilter("all");
                }}
                disabled={loadingBase || saving}
              >
                <option value="">Select Firm</option>
                {firms.map((firm) => (
                  <option key={firm.code} value={firm.code}>
                    {firm.name} ({firm.code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Flow</label>
              <select
                value={flowName}
                onChange={(e) => setFlowName(e.target.value)}
                disabled={loadingBase || saving || !firmCode}
              >
                <option value="">Select Flow</option>
                {flowOptions.map((flow) => (
                  <option key={flow.name} value={flow.name}>
                    {flow.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="beat-setup-grid">
            <div className="beat-setup-panel">
              <div className="panel-title-row">
                <h3>Actors</h3>
                <button type="button" onClick={toggleSelectAllActors}>
                  {allVisibleActorsSelected ? "Unselect All" : "Select All"}
                </button>
              </div>
              <input
                type="text"
                placeholder="Search by name, code, position, role"
                value={actorSearch}
                onChange={(e) => setActorSearch(e.target.value)}
                disabled={saving}
              />
              <div className="panel-list">
                {actorDealerLoading && visibleActors.length === 0 ? (
                  <div className="empty">Loading actors...</div>
                ) : null}
                {visibleActors.map((actor) => (
                  <label key={actor.code} className="list-item">
                    <input
                      type="checkbox"
                      checked={selectedActorCodes.includes(actor.code)}
                      onChange={() => toggleActor(actor.code)}
                      disabled={saving}
                    />
                    <div>
                      <strong>{actor.name || "N/A"}</strong>
                      <span>
                        {actor.code} • {actor.position || actor.role || "NA"}
                      </span>
                    </div>
                  </label>
                ))}
                {visibleActors.length === 0 && <div className="empty">No actors found.</div>}
              </div>
            </div>

            <div className="beat-setup-panel">
              <div className="panel-title-row">
                <h3>Dealer / MDD</h3>
                <button type="button" onClick={toggleSelectAllDealers}>
                  {allVisibleDealerSelected ? "Unselect All" : "Select All"}
                </button>
              </div>

              <div className="dealer-filters">
                <input
                  type="text"
                  placeholder="Search name, code, geo, role"
                  value={dealerSearch}
                  onChange={(e) => setDealerSearch(e.target.value)}
                  disabled={saving}
                />
                <select
                  value={zoneFilter}
                  onChange={(e) => setZoneFilter(e.target.value)}
                  disabled={saving}
                >
                  <option value="">All Zones</option>
                  {zoneOptions.map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </select>
                <select
                  value={districtFilter}
                  onChange={(e) => setDistrictFilter(e.target.value)}
                  disabled={saving}
                >
                  <option value="">All Districts</option>
                  {districtOptions.map((district) => (
                    <option key={district} value={district}>
                      {district}
                    </option>
                  ))}
                </select>
                <select
                  value={talukaFilter}
                  onChange={(e) => setTalukaFilter(e.target.value)}
                  disabled={saving}
                >
                  <option value="">All Talukas</option>
                  {talukaOptions.map((taluka) => (
                    <option key={taluka} value={taluka}>
                      {taluka}
                    </option>
                  ))}
                </select>
                <select
                  value={townFilter}
                  onChange={(e) => setTownFilter(e.target.value)}
                  disabled={saving}
                >
                  <option value="">All Towns</option>
                  {townOptions.map((town) => (
                    <option key={town} value={town}>
                      {town}
                    </option>
                  ))}
                </select>
                <select
                  value={topDealerFilter}
                  onChange={(e) => setTopDealerFilter(e.target.value)}
                  disabled={saving}
                >
                  <option value="all">Top Dealer: All</option>
                  <option value="yes">Top Dealer: Yes</option>
                  <option value="no">Top Dealer: No</option>
                </select>
              </div>

              <div className="selected-count-row">
                Selected: {selectedDealerCodes.length}
                {visibleDealerMdds.length > 0 ? ` • In View: ${selectedVisibleDealerCount}` : ""}
              </div>

              <div className="panel-list">
                {actorDealerLoading && visibleDealerMdds.length === 0 ? (
                  <div className="empty">Loading dealer/MDD...</div>
                ) : null}
                {visibleDealerMdds.map((item) => (
                  <label key={item.code} className="list-item">
                    <input
                      type="checkbox"
                      checked={selectedDealerCodes.includes(item.code)}
                      onChange={() => toggleDealer(item.code)}
                      disabled={saving}
                    />
                    <div>
                      <strong>{item.name || "N/A"}</strong>
                      <span>
                        {item.code} • {item.zone || "NA"} • {item.district || "NA"} •{" "}
                        {item.taluka || "NA"} • {item.town || "NA"} • Top:{" "}
                        {item.topDealer ? "Yes" : "No"}
                      </span>
                    </div>
                  </label>
                ))}
                {visibleDealerMdds.length === 0 && (
                  <div className="empty">No dealer/MDD found.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="beat-setup-modal__footer">
          {error ? <div className="error">{error}</div> : <div />}
          <div className="actions">
            <button type="button" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" onClick={handleSaveAndApply} disabled={saving || loadingBase}>
              {saving ? "Saving..." : "Save Plan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BeatSetupModal;
